import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Settings,
  Palette,
  Database,
  FlaskConical,
  BarChart3
} from 'lucide-react';

export const Status: React.FC = () => {
  // Engine status data
  const engines = [
    { name: 'Knowledge Engine', status: 'complete', progress: 100, description: 'Core system for storing, organizing, and retrieving all knowledge objects' },
    { name: 'Media Engine', status: 'complete', progress: 100, description: 'Manages all media assets and transformations across formats' },
    { name: 'Prospector', status: 'complete', progress: 100, description: 'Discovers and evaluates potential content and opportunities' },
    { name: 'Authoring', status: 'complete', progress: 100, description: 'Provides tools and interfaces for content creation' },
    { name: 'Studio', status: 'complete', progress: 100, description: 'Professional asset creation and design tools' },
    { name: 'Canon', status: 'complete', progress: 100, description: 'Maintains the authoritative source of truth for all content' },
    { name: 'Assembly Engine', status: 'complete', progress: 100, description: 'Composes final deliverables from component parts' },
    { name: 'Publishing Engine', status: 'complete', progress: 100, description: 'Distributes content across channels and platforms' },
    { name: 'Education Engine', status: 'complete', progress: 100, description: 'Delivers learning experiences and tracks progress' },
    { name: 'Orchestration', status: 'complete', progress: 100, description: 'Coordinates workflows across all engines' },
    { name: 'Analytics & Feedback', status: 'complete', progress: 100, description: 'Collects data, generates insights, and drives improvement' },
    { name: 'Identity & Access', status: 'complete', progress: 100, description: 'Manages authentication, authorization, and permissions' },
    { name: 'Curation & Collaboration', status: 'future', progress: 0, description: 'Enables team collaboration and content curation workflows' },
    { name: 'Virtual Studio', status: 'future', progress: 0, description: 'Creates and manages virtual and interactive experiences' },
    { name: 'Influence Engine', status: 'future', progress: 0, description: 'Amplifies reach and measures impact across networks' },
    { name: 'Simulation', status: 'future', progress: 0, description: 'Models scenarios and predicts outcomes' }
  ];

  // Design System status data
  const designSystem = [
    { name: 'Colors', status: 'complete', progress: 100, description: 'Brand color palettes, tints, shades, and usage guidelines' },
    { name: 'Typography', status: 'complete', progress: 100, description: 'Font families, type scale, text styles, and guidelines' },
    { name: 'Icon Library', status: 'in-progress', progress: 75, description: 'Comprehensive icon collection with consistent style' },
    { name: 'Brand Elements', status: 'in-progress', progress: 60, description: 'Logos, patterns, and brand elements' },
    { name: 'Component Library', status: 'in-progress', progress: 40, description: 'Reusable UI components with consistent styling' },
    { name: 'Visual Design Grammar', status: 'in-progress', progress: 80, description: 'Layout principles, spacing, and visual hierarchy' },
    { name: 'Patterns & Background Elements', status: 'planned', progress: 20, description: 'Geometric patterns, overlays, textures, and backgrounds' },
    { name: 'Infographic & Data Visualization', status: 'planned', progress: 15, description: 'Charts, graphs, and data visualization standards' },
    { name: 'Layout & Grid System', status: 'planned', progress: 25, description: 'Spacing, alignment, margins, and white space rules' },
    { name: 'Tone & Voice System', status: 'planned', progress: 30, description: 'Communication guidelines for consistent messaging' },
    { name: 'Best Practices & Design Principles', status: 'planned', progress: 35, description: 'Core design principles and best practices' },
    { name: 'Logo System', status: 'planned', progress: 0, description: 'Complete logo system with variations and usage guidelines' }
  ];

  // Knowledge Architecture status data
  const knowledgeArchitecture = [
    { name: 'M3: Metadata (Grammar)', status: 'complete', progress: 100, description: 'Foundational grammar for describing everything in the system' },
    { name: 'M2: Models (Frameworks)', status: 'complete', progress: 100, description: 'Actual frameworks, models, and systems for decision-making' },
    { name: 'M1: Templates (Applications)', status: 'in-progress', progress: 85, description: 'Ready-to-deploy instances of M2 models' },
    { name: 'M0: Runtime (Execution)', status: 'in-progress', progress: 70, description: 'Live experiences and measurable outcomes' },
    { name: 'Four-Layer Meta-Stack', status: 'complete', progress: 100, description: 'Ontology, Modality, Dynamics, and Evaluation layers' },
    { name: 'Registry Categories', status: 'complete', progress: 100, description: 'Master Organizing Frameworks, Prioritization Frameworks, etc.' },
    { name: 'Content Relationships', status: 'in-progress', progress: 80, description: 'Interconnected content through defined relationships' },
    { name: 'Version Control System', status: 'complete', progress: 100, description: 'Systematic versioning and evolution tracking' }
  ];

  // Lab status data
  const labItems = [
    { name: 'Business Applications', status: 'complete', progress: 100, description: 'Revenue-generating activities and offerings' },
    { name: 'Application Templates', status: 'complete', progress: 100, description: 'Ready-to-use templates for workshops and assessments' },
    { name: 'Geometry Studio', status: 'in-progress', progress: 90, description: 'Visual semantic vocabulary builder' },
    { name: 'Icon Customizer', status: 'in-progress', progress: 85, description: 'Customize and generate icons for the design system' },
    { name: 'Metaphor Studio', status: 'in-progress', progress: 70, description: 'Experiment with core brand metaphors' },
    { name: 'Visual-Verbal Integration', status: 'in-progress', progress: 60, description: 'Comprehensive system for aligning visual and verbal communication' },
    { name: 'Gold Rush Template', status: 'in-progress', progress: 80, description: 'M1 Template for understanding change over time' },
    { name: 'Sentence Builder', status: 'in-progress', progress: 65, description: 'Build contextual sentences and get recommendations' },
    { name: 'Smart Suggestions', status: 'in-progress', progress: 50, description: 'Experimental suggestion engine' },
    { name: 'Source of Truth Checker', status: 'in-progress', progress: 75, description: 'Validate content consistency' },
    { name: 'Semantic Reading Modes', status: 'planned', progress: 30, description: 'Normal vs. semantic reading modes' },
    { name: 'Learning Pathways', status: 'planned', progress: 40, description: 'Structured learning journeys' },
    { name: '3D Shape Combinator', status: 'planned', progress: 25, description: 'Test all possible logical combinations' },
    { name: 'M2 Model Taxonomy', status: 'in-progress', progress: 85, description: 'Comprehensive categorization of M2 model types' },
    { name: 'Team Dashboard', status: 'planned', progress: 35, description: 'Track team activity and learning progress' },
    { name: 'Analytics Dashboard', status: 'planned', progress: 45, description: 'Comprehensive analytics and insights' },
    { name: 'Brand Guidelines Document', status: 'in-progress', progress: 80, description: 'Comprehensive brand guidelines document' },
    { name: 'Teaching Wizard', status: 'planned', progress: 20, description: 'Learn how the system works step-by-step' },
    { name: 'Island Explorer', status: 'planned', progress: 15, description: 'Discover core prioritization capabilities' },
    { name: 'Mountain Ascent', status: 'planned', progress: 10, description: 'Track progress through capability development' },
    { name: 'Plate Stack Component', status: 'planned', progress: 20, description: 'Interactive component for layered information' },
    { name: "What's Next", status: 'planned', progress: 30, description: 'Sequenced next steps under development' },
    { name: 'Framework Notes', status: 'planned', progress: 25, description: 'Personal note-taking and knowledge management' },
    { name: 'Knowledge Navigator', status: 'planned', progress: 40, description: 'AI-powered intelligent guide' },
    { name: 'Progress & Game Mechanics', status: 'planned', progress: 20, description: 'Gamified progress tracking' },
    { name: 'Feature Flags', status: 'planned', progress: 35, description: 'Development tools for feature management' }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-[var(--green)]" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-[var(--mustard)]" />;
      case 'planned':
        return <AlertCircle className="h-5 w-5 text-[var(--text-ter)]" />;
      case 'future':
        return <XCircle className="h-5 w-5 text-[var(--text-ter)]" />;
      default:
        return <AlertCircle className="h-5 w-5 text-[var(--text-ter)]" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]';
      case 'in-progress':
        return 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]';
      case 'planned':
        return 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]';
      case 'future':
        return 'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]';
      default:
        return 'bg-[var(--hover-tint)] text-[var(--text-ter)] border-[var(--rule)]';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-[var(--green)]';
    if (progress >= 60) return 'bg-[var(--mustard)]';
    if (progress >= 40) return 'bg-[var(--slate)]';
    return 'bg-[var(--surface)]';
  };

  const StatusCard: React.FC<{ title: string; icon: React.ComponentType<any>; items: any[] }> = ({ title, icon: Icon, items }) => {
    const completed = items.filter(item => item.status === 'complete').length;
    const inProgress = items.filter(item => item.status === 'in-progress').length;
    const planned = items.filter(item => item.status === 'planned').length;
    const future = items.filter(item => item.status === 'future').length;

    return (
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 bg-consulting-slate-blue-600 flex items-center justify-center">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-charcoal-900">{title}</h2>
            <p className="text-charcoal-600">
              {completed} complete • {inProgress} in progress • {planned} planned • {future} future
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index} className="border border-[var(--rule)] p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  {getStatusIcon(item.status)}
                  <h3 className="font-semibold text-charcoal-900">{item.name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                    {item.status.replace('-', ' ')}
                  </span>
                  <span className="text-sm text-charcoal-600 font-medium">
                    {item.progress}%
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-charcoal-600 mb-3">{item.description}</p>
              
              <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(item.progress)}`}
                  style={{ width: `${item.progress}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/home" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Overview
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="h-16 w-16 bg-consulting-slate-blue-600 flex items-center justify-center shadow-md">
            <BarChart3 className="h-10 w-10 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900">System Status</h1>
            <p className="text-xl text-charcoal-600">Completeness tracking across all system components</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700 max-w-4xl">
          Comprehensive status overview of engines, design system components, knowledge architecture layers, 
          and lab features. Track progress, identify priorities, and monitor system completeness.
        </p>
      </div>

      {/* Status Legend */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-bold text-charcoal-900 mb-4">Status Legend</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-[var(--green)]" />
            <span className="text-sm font-medium text-charcoal-700">Complete (80-100%)</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[var(--mustard)]" />
            <span className="text-sm font-medium text-charcoal-700">In Progress (40-79%)</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-[var(--text-ter)]" />
            <span className="text-sm font-medium text-charcoal-700">Planned (1-39%)</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-[var(--text-ter)]" />
            <span className="text-sm font-medium text-charcoal-700">Future (0%)</span>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid gap-8">
        <StatusCard 
          title="Engines" 
          icon={Settings} 
          items={engines} 
        />
        
        <StatusCard 
          title="Design System" 
          icon={Palette} 
          items={designSystem} 
        />
        
        <StatusCard 
          title="Knowledge Architecture" 
          icon={Database} 
          items={knowledgeArchitecture} 
        />
        
        <StatusCard 
          title="The Lab" 
          icon={FlaskConical} 
          items={labItems} 
        />
      </div>

      {/* Summary Statistics */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">System Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-consulting-slate-blue-600">
              {[...engines, ...designSystem, ...knowledgeArchitecture, ...labItems].filter(item => item.status === 'complete').length}
            </div>
            <div className="text-sm text-charcoal-600">Complete Components</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[var(--mustard)]">
              {[...engines, ...designSystem, ...knowledgeArchitecture, ...labItems].filter(item => item.status === 'in-progress').length}
            </div>
            <div className="text-sm text-charcoal-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[var(--text-ter)]">
              {[...engines, ...designSystem, ...knowledgeArchitecture, ...labItems].filter(item => item.status === 'planned').length}
            </div>
            <div className="text-sm text-charcoal-600">Planned</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[var(--text-ter)]">
              {[...engines, ...designSystem, ...knowledgeArchitecture, ...labItems].filter(item => item.status === 'future').length}
            </div>
            <div className="text-sm text-charcoal-600">Future</div>
          </div>
        </div>
      </div>
    </div>
  );
};
